// ============================================================
// templatesPage.js — Página "Plantillas de Vídeo"
// ============================================================
import { qs, qsa, on, esc } from './utils.js';
import { toast } from './toast.js';
import { uploadAttachment, apiRequest, listHistory, sanitizeMediaUrl } from './api.js';

export function initTemplatesPage() {
  if (document.body.dataset.page !== 'plantillas') return;

  // ── Elementos DOM Principales ──
  const viewCrear = qs('#viewCrearPlantilla');
  const viewCreaciones = qs('#viewMisCreacionesPlantillas');
  const tplCountBadge = qs('#tplCreacionesCount');
  const creacionesGrid = qs('#tplCreacionesGrid');

  // Configuración de la plantilla activa
  const activeTemplateThumb = qs('#activeTemplateThumb');
  const activeTemplateIcon = qs('#activeTemplateIcon');
  const activeTemplateTag = qs('#activeTemplateTag');
  const activeTemplateName = qs('#activeTemplateName');
  const activeTemplateDesc = qs('#activeTemplateDesc');

  // Subida de foto
  const photoDropzone = qs('#tplPhotoDropzone');
  const fileInput = qs('#tplPhotoFileInput');
  const dropzoneEmpty = qs('#tplDropzoneEmpty');
  const dropzoneFilled = qs('#tplDropzoneFilled');
  const previewImg = qs('#tplPreviewImg');
  const btnSelectPhoto = qs('#btnSelectTplPhoto');
  const btnChangePhoto = qs('#btnChangeTplPhoto');
  const btnRemovePhoto = qs('#btnRemoveTplPhoto');

  // Mensaje & Opciones
  const messageInput = qs('#tplMessageInput');
  const charCountEl = qs('#tplCharCount');
  const btnGenerate = qs('#btnGenerateTemplate');

  // Modal de preview
  const previewModal = qs('#tplVideoPreviewModal');
  const modalTitle = qs('#tplModalTitle');
  const modalVideo = qs('#tplModalVideo');
  const btnCloseModal = qs('#btnCloseTplModal');

  // ── Estado Local ──
  let selectedTemplateId = 'tpl-cumpleanos';
  let selectedTemplateTitle = 'Felicitación de Cumpleaños';
  let uploadedPhotoUrl = null;
  let selectedQuality = '720p';
  let selectedAspect = '9:16';
  let isGenerating = false;

  // Sanitizar URLs de los vídeos y posters del catálogo para que apunten al backend correcto
  qsa('.tpl-card').forEach(card => {
    if (card.dataset.video) {
      card.dataset.video = sanitizeMediaUrl(card.dataset.video);
    }
    if (card.dataset.poster) {
      card.dataset.poster = sanitizeMediaUrl(card.dataset.poster);
    }
    const vid = card.querySelector('.tpl-card-video');
    if (vid) {
      if (vid.getAttribute('src')) {
        vid.src = sanitizeMediaUrl(vid.getAttribute('src'));
      }
      if (vid.getAttribute('poster')) {
        vid.poster = sanitizeMediaUrl(vid.getAttribute('poster'));
      }
    }
  });

  // ── 1. Cambio de Pestañas Superiores ──
  on(document, 'click', '[data-action="tpl-tab"]', (e, tabBtn) => {
    const targetTab = tabBtn.dataset.tab;
    qsa('[data-action="tpl-tab"]').forEach(t => t.classList.remove('is-active'));
    tabBtn.classList.add('is-active');

    if (targetTab === 'crear') {
      viewCrear?.classList.remove('is-hidden');
      viewCreaciones?.classList.add('is-hidden');
    } else {
      viewCrear?.classList.add('is-hidden');
      viewCreaciones?.classList.remove('is-hidden');
      loadTemplateCreaciones();
    }
  });

  // ── 2. Selección de Plantilla en el Catálogo ──
  on(document, 'click', '.tpl-card', (e, card) => {
    if (e.target.closest('.tpl-preview-play-btn')) return; // manejado por el botón de play

    qsa('.tpl-card').forEach(c => c.classList.remove('is-selected'));
    card.classList.add('is-selected');

    selectedTemplateId = card.dataset.templateId || 'tpl-cumpleanos';
    selectedTemplateTitle = card.dataset.title || 'Plantilla de vídeo';
    const tag = card.dataset.tag || 'Plantilla';
    const desc = card.dataset.desc || '';
    const poster = card.dataset.poster || '';

    // Actualizar tarjeta lateral con fotograma/poster real
    if (activeTemplateThumb) {
      if (poster) {
        activeTemplateThumb.innerHTML = `<img src="${esc(poster)}" alt="${esc(selectedTemplateTitle)}" style="width:100%; height:100%; object-fit:cover;" />`;
      }
    }
    if (activeTemplateTag) activeTemplateTag.textContent = tag;
    if (activeTemplateName) activeTemplateName.textContent = selectedTemplateTitle;
    if (activeTemplateDesc) activeTemplateDesc.textContent = desc;

    const PLACEHOLDERS = {
      'tpl-cumpleanos': 'Ej: ¡Muchas felicidades Laura por tus 30 años! Que disfrutes mucho de tu día especial.',
      'tpl-noticias': 'Ej: Fuentes oficiales confirman que Marcos se casa el próximo sábado.',
      'tpl-empresa': 'Ej: Reconocimiento especial a Sandra por su extraordinario liderazgo y resultados.',
      'tpl-boda': 'Ej: Para Carlos y Elena en su gran día, ¡que seáis siempre muy felices juntos!',
      'tpl-celebracion': 'Ej: ¡Gran noche de fiesta para celebrar el ascenso de David por todo lo alto!'
    };
    if (messageInput && PLACEHOLDERS[selectedTemplateId]) {
      messageInput.placeholder = PLACEHOLDERS[selectedTemplateId];
    }

    toast({ message: `🎯 Plantilla seleccionada: ${selectedTemplateTitle}`, type: 'info' });
  });

  // ── Reproducción de muestra al pasar el ratón (Hover) ──
  document.addEventListener('mouseover', (e) => {
    const card = e.target.closest('.tpl-card');
    if (card) {
      const vid = card.querySelector('.tpl-card-video');
      if (vid && vid.paused) {
        vid.play().catch(() => {});
      }
    }
  });

  document.addEventListener('mouseout', (e) => {
    const card = e.target.closest('.tpl-card');
    if (card) {
      const vid = card.querySelector('.tpl-card-video');
      if (vid && !vid.paused) {
        vid.pause();
        vid.currentTime = 0;
      }
    }
  });

  // ── 3. Filtros de Categorías del Catálogo ──
  on(document, 'click', '.tpl-cat-btn', (e, btn) => {
    qsa('.tpl-cat-btn').forEach(b => b.classList.remove('is-active'));
    btn.classList.add('is-active');

    const cat = btn.dataset.category || 'all';
    qsa('.tpl-card').forEach(card => {
      if (cat === 'all' || card.dataset.category === cat) {
        card.style.display = 'flex';
      } else {
        card.style.display = 'none';
      }
    });
  });

  // ── 4. Modal de Previsualización de Vídeo de Plantilla ──
  on(document, 'click', '.tpl-preview-play-btn', (e, btn) => {
    e.stopPropagation();
    const card = btn.closest('.tpl-card');
    if (!card) return;

    const videoUrl = sanitizeMediaUrl(card.dataset.video);
    const title = card.dataset.title || 'Muestra de plantilla';

    if (modalTitle) modalTitle.textContent = `Muestra: ${title}`;
    if (modalVideo && videoUrl) {
      modalVideo.src = videoUrl;
      modalVideo.load();
      modalVideo.play().catch(() => {});
    }
    previewModal?.classList.remove('is-hidden');
  });

  function closeModal() {
    if (modalVideo) {
      modalVideo.pause();
      modalVideo.src = '';
    }
    previewModal?.classList.add('is-hidden');
  }

  btnCloseModal?.addEventListener('click', closeModal);
  previewModal?.addEventListener('click', (e) => {
    if (e.target === previewModal) closeModal();
  });

  // ── 5. Gestión de Subida de Foto del Rostro ──
  function handlePhotoFile(file) {
    if (!file || !file.type.startsWith('image/')) {
      toast({ message: '⚠️ Por favor, selecciona un archivo de imagen válido (JPG, PNG o WEBP)', type: 'warning' });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast({ message: '⚠️ La imagen no debe superar los 20 MB', type: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      if (previewImg) previewImg.src = ev.target.result;
      dropzoneEmpty?.classList.add('is-hidden');
      dropzoneFilled?.classList.remove('is-hidden');
    };
    reader.readAsDataURL(file);

    toast({ message: '🚀 Subiendo foto a la IA...', type: 'info' });
    uploadAttachment(file).then(res => {
      uploadedPhotoUrl = (typeof res === 'object' && res.url) ? res.url : (typeof res === 'string' ? res : '');
      toast({ message: '✅ Foto de rostro cargada con éxito', type: 'success' });
    }).catch(err => {
      console.warn('[TEMPLATES] Error al subir foto:', err);
      toast({ message: 'Error al subir la foto al servidor', type: 'error' });
    });
  }

  btnSelectPhoto?.addEventListener('click', () => fileInput?.click());
  btnChangePhoto?.addEventListener('click', () => fileInput?.click());
  fileInput?.addEventListener('change', (e) => {
    const file = e.target.files?.[0];
    if (file) handlePhotoFile(file);
  });

  btnRemovePhoto?.addEventListener('click', () => {
    uploadedPhotoUrl = null;
    if (fileInput) fileInput.value = '';
    if (previewImg) previewImg.src = '';
    dropzoneFilled?.classList.add('is-hidden');
    dropzoneEmpty?.classList.remove('is-hidden');
    toast({ message: 'Foto eliminada', type: 'info' });
  });

  // Drag & drop
  photoDropzone?.addEventListener('dragover', (e) => {
    e.preventDefault();
    photoDropzone.classList.add('is-dragover');
  });
  photoDropzone?.addEventListener('dragleave', () => photoDropzone.classList.remove('is-dragover'));
  photoDropzone?.addEventListener('drop', (e) => {
    e.preventDefault();
    photoDropzone.classList.remove('is-dragover');
    const file = e.dataTransfer.files?.[0];
    if (file) handlePhotoFile(file);
  });

  // ── 6. Contador de Caracteres del Mensaje ──
  messageInput?.addEventListener('input', () => {
    if (charCountEl) charCountEl.textContent = messageInput.value.length;
  });

  // ── 7. Opciones de Formato y Calidad ──
  on(document, 'click', '.tpl-segment-btn', (e, btn) => {
    const group = btn.closest('.tpl-segmented-control')?.dataset.group;
    if (!group) return;

    qsa(`[data-group="${group}"] .tpl-segment-btn`).forEach(b => b.classList.remove('is-active'));
    btn.classList.add('is-active');

    if (group === 'quality') selectedQuality = btn.dataset.value;
    if (group === 'aspect') selectedAspect = btn.dataset.value;
  });

  // ── 8. Generar Vídeo con Plantilla ──
  btnGenerate?.addEventListener('click', async () => {
    if (isGenerating) return;

    // La foto es completamente OPCIONAL
    const hasPhotoUploaded = Boolean(uploadedPhotoUrl || (previewImg && previewImg.src && !previewImg.src.endsWith('#') && dropzoneFilled && !dropzoneFilled.classList.contains('is-hidden')));
    const photoToSend = hasPhotoUploaded ? (uploadedPhotoUrl || previewImg?.src) : null;
    const customMessage = messageInput ? messageInput.value.trim() : '';

    isGenerating = true;
    const originalBtnHtml = btnGenerate.innerHTML;
    btnGenerate.disabled = true;
    btnGenerate.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" style="width:1rem; height:1rem; border-width:2px; margin-right:6px;"></span> Creando vídeo…`;

    try {
      toast({ message: '⏳ Procesando plantilla con la IA...', type: 'info' });

      const response = await apiRequest('/generate/template', {
        method: 'POST',
        body: JSON.stringify({
          templateId: selectedTemplateId,
          templateTitle: selectedTemplateTitle,
          prompt: customMessage || `Vídeo con plantilla: ${selectedTemplateTitle}`,
          photoUrl: photoToSend,
          quality: selectedQuality,
          aspect: selectedAspect,
        }),
      });

      toast({ message: '🚀 ¡Vídeo en proceso! Tu plantilla se está generando.', type: 'success' });

      // Cambiar a la pestaña "Mis Creaciones"
      const tabCreaciones = qs('[data-action="tpl-tab"][data-tab="mis-creaciones"]');
      if (tabCreaciones) tabCreaciones.click();

      await loadTemplateCreaciones();

      if (response && response.jobId && response.status !== 'completed') {
        let attempts = 0;
        const maxAttempts = 40;
        const poll = setInterval(async () => {
          attempts++;
          try {
            const jobData = await apiRequest(`/jobs/${response.jobId}`);
            if (jobData && (jobData.status === 'completed' || jobData.status === 'failed')) {
              clearInterval(poll);
              if (jobData.status === 'completed') {
                toast({ message: '✅ ¡Vídeo de plantilla generado con éxito!', type: 'success' });
              }
              await loadTemplateCreaciones();
            }
          } catch (e) {
            console.warn('[TEMPLATES] Error sondeando trabajo:', e);
          }
          if (attempts >= maxAttempts) {
            clearInterval(poll);
            await loadTemplateCreaciones();
          }
        }, 3000);
      }

    } catch (err) {
      console.error('[TEMPLATES] Error al generar vídeo con plantilla:', err);
      toast({ message: `Error al generar: ${err.message}`, type: 'error' });
    } finally {
      isGenerating = false;
      btnGenerate.disabled = false;
      btnGenerate.innerHTML = originalBtnHtml;
    }
  });

  // ── 9. Cargar Creaciones de Plantillas ──
  async function loadTemplateCreaciones() {
    if (!creacionesGrid) return;

    try {
      const items = await listHistory();
      const templateItems = (items || []).filter(item => item.type === 'template');

      if (tplCountBadge) tplCountBadge.textContent = templateItems.length;

      if (templateItems.length === 0) {
        creacionesGrid.innerHTML = `
          <div style="grid-column: 1 / -1; text-align:center; padding: 48px 20px; background: rgba(255,255,255,0.02); border: 1px solid var(--color-border, rgba(255,255,255,0.08)); border-radius: 16px;">
            <i class="bi bi-collection-play" style="font-size: 3rem; color: #a855f7; display:inline-block; margin-bottom: 12px;"></i>
            <h3 style="color:#fff; font-size:1.1rem; margin-bottom: 6px;">Aún no tienes vídeos con plantillas</h3>
            <p style="color: #a1a1aa; font-size:0.85rem; max-width:400px; margin:0 auto 16px;">Elige una plantilla de cumpleaños, boda o noticias, sube tu foto y crea tu primer vídeo en segundos.</p>
            <button class="mini-btn" type="button" onclick="document.querySelector('[data-action=\\'tpl-tab\\'][data-tab=\\'crear\\']')?.click()">
              <i class="bi bi-magic"></i> Ir al Catálogo de Plantillas
            </button>
          </div>
        `;
        return;
      }

      creacionesGrid.innerHTML = templateItems.map(item => {
        let metaObj = {};
        try { if (item.meta) metaObj = typeof item.meta === 'object' ? item.meta : JSON.parse(item.meta); } catch(e){}

        const tplTitle = metaObj.templateTitle || 'Vídeo con Plantilla';
        const defaultPoster = `/uploads/templates/poster_${(metaObj.templateId || 'tpl-cumpleanos').replace('tpl-', '')}.jpg`;
        const photo = sanitizeMediaUrl(item.thumbnailUrl || metaObj.photoUrl || item.thumb || defaultPoster);
        const videoSrc = sanitizeMediaUrl(item.url || item.resultUrl || '/uploads/templates/tpl_cumpleanos.mp4');
        const dateStr = item.createdAt ? new Date(item.createdAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : 'Reciente';

        return `
          <div class="av-gen-card" style="background: var(--color-bg-surface-2, #1c1c1e); border-radius:14px; overflow:hidden; border: 1px solid rgba(255,255,255,0.08); display:flex; flex-direction:column;">
            <div style="position:relative; width:100%; height:160px; background:#000;">
              ${item.status === 'queued' || item.status === 'processing' ? `
                <div style="position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; background: rgba(0,0,0,0.7); z-index:2;">
                  <span class="spinner-border spinner-border-sm" style="width:2rem; height:2rem; color:#a855f7; border-width:3px; margin-bottom:8px;"></span>
                  <span style="font-size:0.8rem; color:#fff; font-weight:600;">Generando vídeo…</span>
                </div>
              ` : `
                <video src="${esc(videoSrc)}" poster="${esc(photo)}" controls playsinline style="width:100%; height:100%; object-fit:cover;"></video>
              `}
            </div>
            <div style="padding: 12px 14px; flex:1; display:flex; flex-direction:column; justify-content:space-between; gap:8px;">
              <div>
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
                  <span style="font-size:0.7rem; color:#ec4899; font-weight:700; text-transform:uppercase;">${esc(tplTitle)}</span>
                  <span style="font-size:0.72rem; color:#71717a;">${esc(dateStr)}</span>
                </div>
                <p style="font-size:0.82rem; color:#fff; margin:0; line-height:1.3; font-weight:500;">
                  "${esc(item.prompt || 'Plantilla personalizada')}"
                </p>
              </div>
              <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:4px;">
                <a href="${esc(videoSrc)}" download="plantilla_vimo.mp4" target="_blank" class="mini-btn-pill" style="text-decoration:none; font-size:0.72rem;">
                  <i class="bi bi-download"></i> Descargar MP4
                </a>
              </div>
            </div>
          </div>
        `;
      }).join('');

    } catch (e) {
      console.warn('[TEMPLATES] Error cargando creaciones:', e);
    }
  }

  // Refrescar creaciones
  qs('#btnRefreshTplCreaciones')?.addEventListener('click', () => {
    loadTemplateCreaciones();
    toast({ message: '🔄 Creaciones actualizadas', type: 'info' });
  });

  // Carga inicial del contador
  loadTemplateCreaciones();
}
